import { useEffect, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase';

/** Sign in with email + password. Returns a typed error message on failure. */
export async function signInWithPassword(
  email: string,
  password: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

/**
 * Start a Microsoft (Entra ID) sign-in. Staff only: the Azure provider is
 * single-tenant, so only Brix company Microsoft accounts can complete it;
 * customers keep using email + password.
 *
 * The client runs PKCE with detectSessionInUrl OFF (1.37), so the provider
 * comes back to /login?code=… and LoginPage exchanges the code itself
 * (exchangeOAuthCode). ⚠ The redirect must be on the Supabase project's
 * URL allowlist (https://orders.brixbev.com/**), or GoTrue falls back to the
 * project Site URL, which is the gateway.
 */
export async function signInWithMicrosoft(): Promise<{ ok: false; error: string } | void> {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'azure',
    options: { scopes: 'email', redirectTo: `${window.location.origin}/login` },
  });
  if (error) return { ok: false, error: error.message };
}

/** Finish an OAuth sign-in: swap the ?code= for a session. */
export async function exchangeOAuthCode(
  code: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const { error } = await supabase.auth.exchangeCodeForSession(code);
  if (error) return { ok: false, error: error.message };
  return { ok: true };
}

/** Sign out and clear cached session. */
export async function signOut(): Promise<void> {
  await supabase.auth.signOut();
}

/**
 * Subscribe to the current Supabase auth session.
 *
 * - `session === undefined` while we're loading the initial state.
 * - `session === null` after we've confirmed there is no user.
 * - `session === Session` once authenticated.
 *
 * The undefined state matters for the route guard: we shouldn't bounce
 * the user to /login on first paint before getSession() resolves.
 */
export function useSession(): { session: Session | null | undefined; loading: boolean } {
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setSession(data.session ?? null);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next ?? null);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return { session, loading: session === undefined };
}
